// Metrics Controller
//
// Displays charts and numbers by fetching data from the metrics API.
// Supports date filtering, auto-polling, and session persistence.
//
// Usage: https://github.com/cleancar/cleancar-web/issues/1145

import { Controller } from "@hotwired/stimulus"
import Chart from "../config/chartjs"

export default class extends Controller {
  static targets = ["startDate", "endDate", "number", "chart"]
  static values = {
    path: String,
    pollInterval: { type: Number, default: 60000 }
  }

  static colors = [
    '#66CC33', // branding-grün
    '#2B75AE', // branding-blau
    '#CC0000', // branding-rot
    '#21609B', // branding-dunkelblau-1
    // '#3B82F6', // Blue
    // '#10B981', // Green
    // '#F59E0B', // Amber
    // '#EF4444', // Red
    // '#8B5CF6', // Purple
    // '#EC4899', // Pink
    // '#14B8A6', // Teal
    // '#F97316', // Orange
    // '#6366F1', // Indigo
    // '#84CC16'  // Lime
  ]

  connect() {
    this.charts = new Map()
    this.pollingInterval = null
    this.animationTimeouts = new Set()

    this.loadDateRangeFromStorage()
    this.fetchData()
    this.startPolling()
  }

  disconnect() {
    this.destroyAllCharts()
    this.stopPolling()
    this.clearAnimationTimeouts()
  }

  change() {
    this.saveDateRangeToStorage()
    this.fetchData()
  }

  async fetchData() {
    try {
      const url = this.buildUrl()
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.updateView(data)
    } catch (error) {
      console.error('Error fetching metrics data:', error)
    }
  }

  buildUrl() {
    const url = new URL(this.pathValue, window.location.origin)

    if (this.hasStartDateTarget && this.startDateTarget.value) {
      url.searchParams.set('start_date', this.startDateTarget.value)
    }

    if (this.hasEndDateTarget && this.endDateTarget.value) {
      url.searchParams.set('end_date', this.endDateTarget.value)
    }

    return url.toString()
  }

  updateView(data) {
    this.updateNumbers(data)
    this.updateCharts(data)
  }

  updateNumbers(data) {
    this.numberTargets.forEach(element => {
      const key = element.dataset.key
      if (data[key] !== undefined) {
        this.animateNumberChange(element, data[key])
      }
    })
  }

  animateNumberChange(element, newValue) {
    element.style.transition = 'opacity 0.3s ease-in-out'
    element.style.opacity = '0.5'

    const timeoutId = setTimeout(() => {
      element.textContent = newValue
      element.style.opacity = '1'
      this.animationTimeouts.delete(timeoutId)
    }, 150)

    this.animationTimeouts.add(timeoutId)
  }

  updateCharts(data) {
    this.chartTargets.forEach(canvas => {
      const key = canvas.dataset.key
      const type = canvas.dataset.type || 'pie'

      if (data[key] !== undefined) {
        this.updateChart(canvas, data[key], type)
      }
    })
  }

  updateChart(canvas, chartData, type) {
    const chartId = canvas.dataset.key

    if (this.charts.has(chartId)) {
      const chart = this.charts.get(chartId)
      chart.data = this.prepareChartData(chartData)
      chart.update('active')
    } else {
      const chart = new Chart(canvas, {
        type: type,
        data: this.prepareChartData(chartData),
        options: this.getChartOptions(type)
      })
      this.charts.set(chartId, chart)
    }
  }

  prepareChartData(data) {
    const labels = Object.keys(data)
    const values = Object.values(data)

    return {
      labels: labels,
      datasets: [{
        data: values,
        backgroundColor: this.getChartColors(labels.length),
        borderWidth: 2,
        borderColor: '#ffffff'
      }]
    }
  }

  getChartColors(count) {
    return Array.from(
      { length: count },
      (_, i) => this.constructor.colors[i % this.constructor.colors.length]
    )
  }

  getChartOptions(type) {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: true,
      animation: {
        duration: 750,
        easing: 'easeInOutQuart'
      },
      plugins: {
        legend: {
          display: type === 'pie' || type === 'doughnut',
          position: 'bottom'
        }
      }
    }

    if (type === 'bar' || type === 'line') {
      baseOptions.scales = {
        y: {
          beginAtZero: true
        }
      }
    }

    if (type === 'doughnut') {
      baseOptions.cutout = '70%'
      baseOptions.rotation = -100
      baseOptions.circumference = 200
    }

    return baseOptions
  }

  destroyAllCharts() {
    this.charts.forEach(chart => chart.destroy())
    this.charts.clear()
  }

  startPolling() {
    this.pollingInterval = setInterval(() => {
      this.fetchData()
    }, this.pollIntervalValue)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  clearAnimationTimeouts() {
    this.animationTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
    this.animationTimeouts.clear()
  }

  saveDateRangeToStorage() {
    const dateRange = {
      start_date: this.hasStartDateTarget ? this.startDateTarget.value : null,
      end_date: this.hasEndDateTarget ? this.endDateTarget.value : null
    }
    localStorage.setItem(this.storageKey, JSON.stringify(dateRange))
  }

  loadDateRangeFromStorage() {
    const storedData = localStorage.getItem(this.storageKey)

    if (storedData) {
      try {
        const dateRange = JSON.parse(storedData)

        if (this.hasStartDateTarget && dateRange.start_date) {
          this.startDateTarget.value = dateRange.start_date
        }

        if (this.hasEndDateTarget && dateRange.end_date) {
          this.endDateTarget.value = dateRange.end_date
        }
      } catch (error) {
        console.error('Error loading date range from storage:', error)
      }
    }
  }

  get storageKey() {
    return `metrics_date_range_${this.pathValue}`
  }
}
